import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError } from '../lib/api.js';
import { useClients } from '../lib/clients.js';
import { formatDate } from '../lib/format.js';

/* ============================================================================
   /clients — everyone who has paid us.
   ----------------------------------------------------------------------------
   A single column of cards rather than a table. A table of eight columns on a
   360px Android is a horizontal scrollbar, and this screen gets read on a
   phone like every other one here.
   ========================================================================== */

export function ClientsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);

  const { data, isPending, error } = useClients(q);

  function applySearch(value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set('q', value);
    else next.delete('q');
    setParams(next, { replace: true });
  }

  const items = data?.items ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Clients</h1>
          <p className="muted">
            {data ? `${data.total} client` : 'Loading…'}
            {data && data.total > items.length ? ` — ${items.length} shown` : ''}
          </p>
        </div>

        <form
          className="row row--search"
          onSubmit={(event) => {
            event.preventDefault();
            applySearch(search.trim());
          }}
        >
          <div className="field field--search">
            <label htmlFor="q" className="sr-only">
              Search by business, name, number or GSTIN
            </label>
            <input
              id="q"
              type="search"
              placeholder="Business, name or number"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <button className="btn btn--ghost btn--small" type="submit">
            Search
          </button>
        </form>
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load clients.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      {data && items.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>{q ? 'No client matches that.' : 'No clients yet.'}</p>
          {!q ? (
            /* Point at the next action rather than leaving an empty box. A
               client is made by converting a won lead, so that is where
               someone looking at this screen needs to go. */
            <>
              <p className="muted" style={{ margin: '6px 0 0' }}>
                Turn a won lead into a client — that is where a client starts.
              </p>
              <Link to="/leads?status=won" className="btn btn--small" style={{ marginTop: 12 }}>
                See won leads
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="stack">
        {items.map((client) => (
          <article className="card" key={client.id}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <Link to={`/clients/${client.id}`} className="lead-card__business">
                  {client.business}
                </Link>
                <div className="lead-card__meta">
                  {client.ownerName} · {client.phone}
                </div>
              </div>

              <div className="row" style={{ gap: 6 }}>
                {client.overdueCount > 0 ? (
                  <span className="pill pill--warn">
                    {client.overdueCount} late
                    <span className="sr-only"> project past its promised date</span>
                  </span>
                ) : null}
                {client.carePlanActive ? <span className="pill">Care plan</span> : null}
                <span className="pill pill--muted">
                  {client.projectCount} project{client.projectCount === 1 ? '' : 's'}
                </span>
              </div>
            </div>

            <div className="lead-card__meta" style={{ marginTop: 8 }}>
              Client since {formatDate(client.createdAt)}
              {client.gstin ? ` · GSTIN ${client.gstin}` : ''}
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
